import { Fragment } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import type { EditorState } from 'draft-js';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { FormLabel } from '@components/form-label';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { TextEditor, useEditorState } from '@components/text-editor';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';

import { FixedBottomActions } from 'components/FixedBottomActions';
import { Header } from 'components/headers';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { TextEditorToolbar } from 'components/TextEditorToolbar';
import { usePlaceholderMap } from 'hooks/use-placeholder-map';
import { useModel } from 'hooks/useModel';
import { useStore } from 'models/Provider';
import { EmailTemplateEdit as EmailTemplateEditModel } from 'models/templates';
import type { IEmailTemplate } from 'models/types';

export const EditEmailTemplate = observer(function EmailTemplateEdit() {
  const { templateStore } = useStore();
  const { templateID } = useParams();

  if (templateID === 'new') {
    return <EditForm />;
  }

  const emailTemplate = templateStore.emailTemplates.find(
    ({ id }: { id: number }) => id === Number(templateID),
  );

  if (!emailTemplate) {
    return <Navigate to="/settings/templates/emails" replace />;
  }

  return <EditForm template={emailTemplate} />;
});

const EditForm = observer(function EditForm({ template }: { template?: IEmailTemplate }) {
  const { t, api, templateStore, router } = useStore();
  const { showError, showSuccess } = useToaster();

  const og = template
    ? getSnapshot(template)
    : { id: -1, templateType: 'applicant_new_application' };
  const model = useModel(
    EmailTemplateEditModel,
    { original: og },
    { api, showError, showSuccess, t, templateStore, router },
  );
  const { editorState, setEditorState, resetEditorState } = useEditorState(model.contentHtml);
  const placeholderMap = usePlaceholderMap(model.templateType);

  function updateEditorState(newState: EditorState) {
    setEditorState(newState);
    model.setContentFromEditorState(newState);
  }

  const typeItems = ['applicant_new_application', 'rejection', 'message'].map((type) => ({
    value: type,
    label: t(`company.templates.type.${type}`),
  }));
  const queryClient = useQueryClient();

  return (
    <Fragment>
      <Header
        // @ts-ignore
        breadcrumbs={<Crumbs template={template} />}
      />
      <PageContent>
        {model.saving && <Loading />}
        <Container space={4} orientation="vertical">
          <TextField
            label={t('company.templates.email.label.title')}
            placeholder={t('company.templates.email.title.placeholder')}
            value={model.title}
            onChange={model.setTitle}
            isDisabled={model.saving}
          />
          <div>
            <Stack space={1}>
              <FormLabel text={t('company.templates.email.label.message')} />
              <TextEditor
                variant="gray"
                disabled={model.saving}
                editorState={editorState}
                onChange={updateEditorState}
              >
                <Stack orientation="horizontal" space={2} justify="space-between" align="center">
                  <TextEditorToolbar />
                  <TextEditor.PlaceholderMenu
                    label={t('editor.placeholder.title')}
                    placeholderMap={placeholderMap}
                  />
                </Stack>
                <TextEditor.Input placeholder={t('company.templates.email.message.placeholder')} />
              </TextEditor>
            </Stack>
          </div>
          <Select
            isClearable={false}
            isSearchable={false}
            label={t('company.templates.email.label.type')}
            isDisabled={model.saving}
            defaultValue={model.templateType}
            placeholder={t('company.templates.email.type.title')}
            options={typeItems}
            onChange={model.setType}
          />
          {model.templateType === 'applicant_new_application' && (
            <Checkbox
              label={t('company.templates.email.label.setdefault')}
              value={model.isDefault}
              onChange={model.toggleDefault}
            />
          )}
        </Container>
        <FixedBottomActions>
          {model.dirty && model.original.id !== -1 && (
            <Button
              variant="secondary"
              isDisabled={model.saving}
              onClick={() => {
                model.reset();
                resetEditorState();
              }}
              text={t('company.templates.email.button.cancel')}
            />
          )}

          {(!model.dirty || model.original.id === -1) && (
            <Link to="/settings/templates/emails">
              <Button variant="secondary" text={t('company.templates.email.button.back')} />
            </Link>
          )}

          <Button
            onClick={() => {
              model.save();
              queryClient.invalidateQueries(['email-templates']);
            }}
            isDisabled={!model.canSave}
            text={t('company.templates.email.button.save')}
          />
        </FixedBottomActions>
      </PageContent>
    </Fragment>
  );
});

const Crumbs = observer(({ template }: { template?: IEmailTemplate }) => {
  const { t } = useStore();

  let items = [
    { label: t('company.templates.crumbs.root'), link: '/settings/templates' },
    { label: t('company.templates.tabs.emails'), link: '/settings/templates/emails' },
  ];

  if (template) {
    items.push({ label: template.title || template.name, link: '' });
  } else {
    items.push({ label: t('company.templates.crumbs.new'), link: '' });
  }

  return <Breadcrumb items={items} />;
});

const Container = styled(Stack)({
  maxWidth: 580,
  margin: 'auto',
});
